// ============================================================================
// nameVariants.js — a nevek írásmód-változatainak kiválasztása.
//
// A szerver azonos szabállyal dolgozik (functions/src/lib/nameVariants.js):
// a kettőnek MINDIG ugyanazt kell adnia, különben ugyanaz a név máshogy
// jelenne meg a listában és az értesítésben.
//
// SZÁNDÉKOS KORLÁT: a rendszer nem talál ki névalakot. Ahol a tag nem adott
// meg változatot, ott mindenki az EREDETI nevet látja - egy rosszul
// kitalált olvasat (山崎 = Yamazaki VAGY Yamasaki) rosszabb, mint az
// olvasatlan, de hiteles név.
//
// HAT ÍRÁSRENDSZER (2026-09-30, tagi kérésre): nameLatn (latin), nameJa
// (japán), nameZh (kínai), nameKo (koreai/hangul), nameCyr (cirill), nameEl
// (görög). A japán ÉS a kínai is a han (kandzsi/hanzi) írásjegyeket
// használja, ezért van a kettőnek KÜLÖN mezője - emberi szemmel gyakran
// megkülönböztethetetlenek, de más a kiejtésük/átírásuk.
// ============================================================================

const JAPANESE_RE = /[\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/u;
const HANGUL_RE = /[\uac00-\ud7a3\u1100-\u11ff\u3130-\u318f]/u;
const HAN_RE = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/u;
const CYRILLIC_RE = /[\u0400-\u04ff\u0500-\u052f]/u;
const GREEK_RE = /[\u0370-\u03ff\u1f00-\u1fff]/u;

/** Tartalmaz-e japán írásjegyet (kana vagy kandzsi)? */
export function hasJapanese(text) {
  return JAPANESE_RE.test(String(text || ""));
}

/** Tartalmaz-e han (kandzsi/hanzi) írásjegyet? Japán ÉS kínai is ezt használja. */
export function hasHan(text) {
  return HAN_RE.test(String(text || ""));
}

/** Tartalmaz-e koreai (hangul) írásjegyet? */
export function hasHangul(text) {
  return HANGUL_RE.test(String(text || ""));
}

/** Tartalmaz-e cirill betűt? */
export function hasCyrillic(text) {
  return CYRILLIC_RE.test(String(text || ""));
}

/** Tartalmaz-e görög betűt? */
export function hasGreek(text) {
  return GREEK_RE.test(String(text || ""));
}

// Nyelv → írásmód - lásd functions/src/lib/nameVariants.js (a két fájlnak
// MINDIG ugyanazt kell adnia). NEM a felület nyelve dönt, hanem az, hogy
// MILYEN NYELVRE van (vagy lenne) fordítva az olvasott üzenet. A "zh" előtag
// a "zh-Hans"-t ÉS a "zh-Hant"-ot is lefedi (a kötőjel előtti rész számít).
const SCRIPT_BY_LANG_PREFIX = {
  ja: "ja",
  zh: "zh",
  ko: "ko",
  el: "el",
  ru: "cyr", bg: "cyr", uk: "cyr", be: "cyr", sr: "cyr", mk: "cyr", kk: "cyr", ky: "cyr", tg: "cyr", mn: "cyr",
};

/** Melyik írásmódot kell mutatni ehhez a nyelvhez? */
export function scriptForLang(lang) {
  const prefix = String(lang || "").toLowerCase().split("-")[0];
  return SCRIPT_BY_LANG_PREFIX[prefix] || "latin";
}

/**
 * Az olvasónak megfelelő névalak.
 *
 * A japán célnyelv KIVÉTEL: ott a latin alak nem tartalék - vagy megvan a
 * japán változat, vagy az eredeti név marad. A kínai (és a koreai, cirill,
 * görög) célnyelvnél a latin alak a tartalék, ha a saját változat hiányzik.
 *
 * @param {{name?: string, nameLatn?: string, nameJa?: string, nameZh?: string, nameKo?: string, nameCyr?: string, nameEl?: string}} nev
 * @param {string} viewerLang az a nyelv, amelyikre az üzenet le van (vagy
 *   lenne) fordítva - NEM feltétlenül a felület nyelve.
 */
export function pickName(nev, viewerLang) {
  const alak = (mezo) => (nev && typeof nev[mezo] === "string" && nev[mezo].trim()) || "";
  const base = alak("name");
  const latn = alak("nameLatn");

  switch (scriptForLang(viewerLang)) {
    case "ja":
      return alak("nameJa") || base;
    case "zh":
      return alak("nameZh") || latn || base;
    case "ko":
      return alak("nameKo") || latn || base;
    case "cyr":
      return alak("nameCyr") || latn || base;
    case "el":
      return alak("nameEl") || latn || base;
    default:
      return latn || base;
  }
}

/**
 * Egy ÜZENET küldőjének neve az olvasó szerint. Az üzenet a küldés
 * pillanatában rögzítette a változatokat (lásd sendMessage).
 */
export function pickSenderName(msg, viewerLang) {
  return pickName(
    {
      name: msg.senderName,
      nameLatn: msg.senderNameLatn,
      nameJa: msg.senderNameJa,
      nameZh: msg.senderNameZh,
      nameKo: msg.senderNameKo,
      nameCyr: msg.senderNameCyr,
      nameEl: msg.senderNameEl,
    },
    viewerLang
  );
}

// ---------------------------------------------------------------------------
// Írásrendszer-felismerés — a szerverrel AZONOS szabály
// (functions/src/lib/nameVariants.js). A kettőnek mindig ugyanazt kell
// adnia, ezért a teszt összeveti őket.
//
// A kandzsi és a kínai írásjegyek ugyanabban az Unicode-tartományban
// vannak, ezért a csupa-han név TÖBBÉRTELMŰ: 王明 lehet japán, kínai vagy
// koreai. Ilyenkor megkérdezzük a tagot, nem tippelünk.
// ---------------------------------------------------------------------------
const KANA_RE = /[\u3040-\u309f\u30a0-\u30ff\uff66-\uff9f]/u;

export function detectNameScript(text) {
  const s = String(text || "").trim();
  if (s === "") return "ures";
  if (HANGUL_RE.test(s)) return "hangul";
  if (KANA_RE.test(s)) return "kana";
  if (HAN_RE.test(s)) return "han";
  return "latin";
}

export function needsLanguageChoice(text) {
  return detectNameScript(text) === "han";
}
